/**
 * HealthCheckCard Component
 * Displays individual service health status
 */

import React from 'react';
import type { ServiceHealth, ServiceType } from '../../types/health.types';
import './HealthCheckCard.css';

interface HealthCheckCardProps {
  serviceName: string;
  serviceType: ServiceType;
  serviceData: ServiceHealth;
  className?: string;
}

const HealthCheckCard: React.FC<HealthCheckCardProps> = ({
  serviceName,
  serviceData,
  className = '',
}) => {
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'disconnected':
        return 'warning';
      case 'error':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case 'connected':
        return '✅';
      case 'disconnected':
        return '⚠️';
      case 'error':
        return '❌';
      default:
        return '❓';
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  return (
    <div className={`health-card ${className}`}>
      <div className="health-card__header">
        <div className="health-card__title">
          <span className="health-card__icon">{getStatusIcon(serviceData.status)}</span>
          <h3 className="health-card__name">{serviceName}</h3>
        </div>
        <div className={`health-card__status health-card__status--${getStatusColor(serviceData.status)}`}>
          {serviceData.status}
        </div>
      </div>

      <div className="health-card__body">
        {serviceData.response_time_ms !== undefined && (
          <div className="health-card__metric">
            <span className="health-card__metric-label">Response Time:</span>
            <span className="health-card__metric-value">
              {serviceData.response_time_ms}ms
            </span>
          </div>
        )}

        <div className="health-card__metric">
          <span className="health-card__metric-label">Last Checked:</span>
          <span className="health-card__metric-value">
            {formatTimestamp(serviceData.last_checked)}
          </span>
        </div>

        {serviceData.error_message && (
          <div className="health-card__error">
            <span className="health-card__error-label">Error:</span>
            <span className="health-card__error-message">
              {serviceData.error_message}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthCheckCard;